/**
 * Answer caching (board 25: K01 policy, K07 invalidation, K08 outages, K10 metrics) in the four
 * launch languages. Arabic, Hindi and Urdu are pending review by native speakers.
 */
export const cachingEn = {
  nav: 'Answer caching',
  title: 'Answer caching',
  description:
    'Reuse answers to common opening questions, so customers get them faster and at lower cost. Personal, live and sensitive questions are never cached.',
  brand: 'Brand',
  noBrands: 'Add a brand first.',
  loading: 'Loading answer caching',
  noManage: 'You can look, but only people who manage knowledge can change caching.',
  tiers: {
    exact_answer: 'Exact answers',
    embedding: 'Search embeddings',
    nlp: 'Language analysis',
    retrieval: 'Search results',
    translation: 'Translations',
  },
  tierHints: {
    exact_answer:
      'The same first question, in the same language, gets the same answer while the knowledge, agent and policy behind it stay the same.',
    embedding: 'Knowledge search reuses the numbers computed for the same question.',
    nlp: 'Not used yet.',
    retrieval: 'Not used yet.',
    translation: 'Not used yet.',
  },
  classes: {
    general: 'General',
    public_knowledge: 'Public knowledge',
    internal_knowledge: 'Internal knowledge',
    personal: 'Personal data',
    identity: 'Identity',
    account: 'Account details',
    memory: 'Customer memory',
    live_order: 'Live orders',
    payment: 'Payments',
    approval: 'Approvals',
    tool_result: 'Tool results',
  },
  policy: {
    title: 'What is cached',
    version: 'Policy version {{version}}',
    use: 'Use {{tier}}',
    ttl: 'Keep for (minutes)',
    ttlHint: 'From 1 minute to 30 days (43200 minutes).',
    neverCached: 'Never cached, whatever the settings',
    save: 'Save policy',
    saved: 'Policy saved. Answers stored under the old policy are no longer used.',
  },
  inspect: {
    title: 'Test a question',
    description:
      'What would happen if a visitor opened a chat with this question. Nothing is counted or stored.',
    question: 'Question',
    language: 'Reply language',
    auto: 'Detect from the question',
    run: 'Check',
    result: 'Result',
    status: {
      hit: 'Answered from the cache',
      miss: 'Not cached yet',
      stale: 'The cached answer is out of date',
      bypass: 'Never cached',
      unavailable: 'The cache is not reachable',
    },
    reasons: {
      personal_data: 'The question contains personal data.',
      personalized: 'Only the first message of a conversation can be shared.',
      tier_disabled: 'Exact answers are turned off for this brand.',
      excluded_class: 'It uses data that is never cached.',
      excluded_by_rule: 'A rule excludes it, for example live order questions.',
      side_effects: 'It could change something.',
      incomplete: 'The answer was not complete.',
      no_published_agent: 'The brand has no published AI agent.',
      dependency_changed: 'The knowledge, agent or policy changed after it was stored.',
      store_unavailable: 'Answers are computed fresh until it is back.',
      not_cached: 'No answer is stored for this question yet.',
    },
    fingerprint: 'Fingerprint',
    expires: 'Expires',
    knowledge: 'Knowledge versions',
    version: 'version {{version}}',
    preview: 'Stored answer',
    sources: 'Sources',
  },
  entries: {
    title: 'Stored answers',
    description: 'Fingerprints and sources only. Questions and answers are not shown here.',
    empty: 'No stored answers.',
    fingerprint: 'Fingerprint',
    language: 'Language',
    sources: 'Sources',
    hits: 'Reused',
    stored: 'Stored',
    expires: 'Expires',
    state: 'State',
    states: {
      active: 'In use',
      stale: 'Out of date',
      invalidated: 'No longer used',
    },
    reason: 'Reason: {{reason}}',
    noSources: 'No sources',
  },
  invalidate: {
    title: 'Stop reusing answers',
    description:
      'Use this after a correction. Reads stop at once; stored copies are removed afterwards.',
    source: 'Answers that cite',
    allSources: 'All stored answers of this brand',
    reason: 'Reason',
    reasonHint: 'Kept in the audit log.',
    impact: 'Answers affected: {{count}}',
    submit: 'Stop reusing',
    confirmTitle: 'Stop reusing these answers?',
    confirmBody:
      'Stored answers will no longer be used. The next customers get answers computed fresh.',
    done: 'Stopped reusing {{count}} answers.',
    failed: '{{count}} stored copies could not be removed yet. They are no longer read.',
  },
  metrics: {
    title: 'Use and health',
    description: 'Counts only, per day, tier and language.',
    healthy: 'The cache is reachable.',
    degraded: 'The cache is not reachable. Answers are computed fresh.',
    failures: 'Failed operations in the last 15 minutes: {{count}}',
    degradedHint:
      'Customers still get answers, but they cost more. If wrong answers were reused, turn the tier off above.',
    empty: 'No use recorded yet.',
    day: 'Day',
    tier: 'Tier',
    language: 'Language',
    hits: 'Reused',
    misses: 'Computed',
    bypasses: 'Not cacheable',
    stale: 'Out of date',
    errors: 'Errors',
    hitRate: 'Reuse rate',
    savedTokens: 'Tokens saved',
    savedTime: 'Time saved',
    noLanguage: 'Any',
  },
} as const;

type Strings<T> = { [K in keyof T]: T[K] extends string ? string : Strings<T[K]> };
export type CachingText = Strings<typeof cachingEn>;

export const cachingAr: CachingText = {
  nav: 'حفظ الإجابات',
  title: 'حفظ الإجابات',
  description:
    'أعد استخدام إجابات الأسئلة الافتتاحية الشائعة، ليحصل عليها العملاء أسرع وبتكلفة أقل. لا تُحفظ أبدًا الأسئلة الشخصية أو الآنية أو الحساسة.',
  brand: 'العلامة التجارية',
  noBrands: 'أضف علامة تجارية أولًا.',
  loading: 'جارٍ تحميل حفظ الإجابات',
  noManage: 'يمكنك الاطلاع، لكن من يدير المعرفة وحدهم يستطيعون تغيير الحفظ.',
  tiers: {
    exact_answer: 'الإجابات المطابقة',
    embedding: 'متجهات البحث',
    nlp: 'تحليل اللغة',
    retrieval: 'نتائج البحث',
    translation: 'الترجمات',
  },
  tierHints: {
    exact_answer:
      'السؤال الأول نفسه، باللغة نفسها، يحصل على الإجابة نفسها ما دامت المعرفة والمساعد والسياسة وراءها لم تتغير.',
    embedding: 'يعيد بحث المعرفة استخدام الأرقام المحسوبة للسؤال نفسه.',
    nlp: 'غير مستخدم بعد.',
    retrieval: 'غير مستخدم بعد.',
    translation: 'غير مستخدم بعد.',
  },
  classes: {
    general: 'عام',
    public_knowledge: 'معرفة عامة',
    internal_knowledge: 'معرفة داخلية',
    personal: 'بيانات شخصية',
    identity: 'الهوية',
    account: 'تفاصيل الحساب',
    memory: 'ذاكرة العميل',
    live_order: 'الطلبات الجارية',
    payment: 'المدفوعات',
    approval: 'الموافقات',
    tool_result: 'نتائج الأدوات',
  },
  policy: {
    title: 'ما الذي يُحفظ',
    version: 'إصدار السياسة {{version}}',
    use: 'استخدام {{tier}}',
    ttl: 'مدة الحفظ (بالدقائق)',
    ttlHint: 'من دقيقة واحدة إلى 30 يومًا (43200 دقيقة).',
    neverCached: 'لا يُحفظ أبدًا مهما كانت الإعدادات',
    save: 'حفظ السياسة',
    saved: 'حُفظت السياسة. لم تعد الإجابات المحفوظة بالسياسة القديمة مستخدمة.',
  },
  inspect: {
    title: 'اختبر سؤالًا',
    description: 'ما سيحدث لو بدأ زائر محادثة بهذا السؤال. لا يُحسب أو يُحفظ شيء.',
    question: 'السؤال',
    language: 'لغة الرد',
    auto: 'اكتشاف من السؤال',
    run: 'تحقّق',
    result: 'النتيجة',
    status: {
      hit: 'يُجاب من الذاكرة المؤقتة',
      miss: 'غير محفوظ بعد',
      stale: 'الإجابة المحفوظة قديمة',
      bypass: 'لا يُحفظ أبدًا',
      unavailable: 'تعذّر الوصول إلى الذاكرة المؤقتة',
    },
    reasons: {
      personal_data: 'يحتوي السؤال على بيانات شخصية.',
      personalized: 'لا يمكن مشاركة إلا الرسالة الأولى من المحادثة.',
      tier_disabled: 'الإجابات المطابقة متوقفة لهذه العلامة التجارية.',
      excluded_class: 'يستخدم بيانات لا تُحفظ أبدًا.',
      excluded_by_rule: 'تستبعده قاعدة، مثل أسئلة الطلبات الجارية.',
      side_effects: 'قد يغيّر شيئًا.',
      incomplete: 'لم تكن الإجابة كاملة.',
      no_published_agent: 'ليس لدى العلامة التجارية مساعد ذكاء اصطناعي منشور.',
      dependency_changed: 'تغيّرت المعرفة أو المساعد أو السياسة بعد الحفظ.',
      store_unavailable: 'تُحسب الإجابات من جديد حتى يعود.',
      not_cached: 'لا توجد إجابة محفوظة لهذا السؤال بعد.',
    },
    fingerprint: 'البصمة',
    expires: 'ينتهي',
    knowledge: 'إصدارات المعرفة',
    version: 'الإصدار {{version}}',
    preview: 'الإجابة المحفوظة',
    sources: 'المصادر',
  },
  entries: {
    title: 'الإجابات المحفوظة',
    description: 'البصمات والمصادر فقط. لا تظهر الأسئلة والإجابات هنا.',
    empty: 'لا توجد إجابات محفوظة.',
    fingerprint: 'البصمة',
    language: 'اللغة',
    sources: 'المصادر',
    hits: 'مرات الاستخدام',
    stored: 'حُفظت',
    expires: 'تنتهي',
    state: 'الحالة',
    states: {
      active: 'مستخدمة',
      stale: 'قديمة',
      invalidated: 'لم تعد مستخدمة',
    },
    reason: 'السبب: {{reason}}',
    noSources: 'لا مصادر',
  },
  invalidate: {
    title: 'إيقاف إعادة استخدام الإجابات',
    description: 'استخدم هذا بعد تصحيح. تتوقف القراءة فورًا، وتُحذف النسخ المحفوظة بعد ذلك.',
    source: 'الإجابات التي تستشهد بـ',
    allSources: 'كل الإجابات المحفوظة لهذه العلامة التجارية',
    reason: 'السبب',
    reasonHint: 'يُحفظ في سجل التدقيق.',
    impact: 'الإجابات المتأثرة: {{count}}',
    submit: 'أوقف إعادة الاستخدام',
    confirmTitle: 'إيقاف إعادة استخدام هذه الإجابات؟',
    confirmBody:
      'لن تُستخدم الإجابات المحفوظة بعد الآن. سيحصل العملاء التالون على إجابات محسوبة من جديد.',
    done: 'أُوقفت إعادة استخدام {{count}} إجابة.',
    failed: 'تعذّر حذف {{count}} نسخة محفوظة بعد. لم تعد تُقرأ.',
  },
  metrics: {
    title: 'الاستخدام والحالة',
    description: 'أعداد فقط، حسب اليوم والنوع واللغة.',
    healthy: 'يمكن الوصول إلى الذاكرة المؤقتة.',
    degraded: 'تعذّر الوصول إلى الذاكرة المؤقتة. تُحسب الإجابات من جديد.',
    failures: 'العمليات الفاشلة في آخر 15 دقيقة: {{count}}',
    degradedHint:
      'ما زال العملاء يحصلون على إجابات، لكن بتكلفة أعلى. إن أُعيد استخدام إجابات خاطئة، أوقف هذا النوع في الأعلى.',
    empty: 'لم يُسجَّل استخدام بعد.',
    day: 'اليوم',
    tier: 'النوع',
    language: 'اللغة',
    hits: 'أُعيد استخدامها',
    misses: 'حُسبت',
    bypasses: 'غير قابلة للحفظ',
    stale: 'قديمة',
    errors: 'الأخطاء',
    hitRate: 'نسبة إعادة الاستخدام',
    savedTokens: 'الرموز الموفَّرة',
    savedTime: 'الوقت الموفَّر',
    noLanguage: 'أي لغة',
  },
};

export const cachingHi: CachingText = {
  nav: 'जवाबों का कैश',
  title: 'जवाबों का कैश',
  description:
    'आम शुरुआती सवालों के जवाब दोबारा इस्तेमाल करें, ताकि ग्राहकों को वे जल्दी और कम खर्च में मिलें। निजी, लाइव और संवेदनशील सवाल कभी कैश नहीं होते।',
  brand: 'ब्रांड',
  noBrands: 'पहले एक ब्रांड जोड़ें।',
  loading: 'जवाबों का कैश लोड हो रहा है',
  noManage: 'आप देख सकते हैं, पर कैश सिर्फ़ ज्ञान संभालने वाले लोग बदल सकते हैं।',
  tiers: {
    exact_answer: 'हूबहू जवाब',
    embedding: 'खोज एम्बेडिंग',
    nlp: 'भाषा विश्लेषण',
    retrieval: 'खोज नतीजे',
    translation: 'अनुवाद',
  },
  tierHints: {
    exact_answer:
      'एक ही पहला सवाल, एक ही भाषा में, तब तक एक ही जवाब पाता है जब तक उसके पीछे का ज्ञान, एजेंट और नीति न बदले।',
    embedding: 'ज्ञान खोज उसी सवाल के लिए निकाले गए अंक दोबारा इस्तेमाल करती है।',
    nlp: 'अभी इस्तेमाल नहीं होता।',
    retrieval: 'अभी इस्तेमाल नहीं होता।',
    translation: 'अभी इस्तेमाल नहीं होता।',
  },
  classes: {
    general: 'सामान्य',
    public_knowledge: 'सार्वजनिक ज्ञान',
    internal_knowledge: 'आंतरिक ज्ञान',
    personal: 'निजी जानकारी',
    identity: 'पहचान',
    account: 'खाते की जानकारी',
    memory: 'ग्राहक की याद',
    live_order: 'चालू ऑर्डर',
    payment: 'भुगतान',
    approval: 'मंज़ूरियाँ',
    tool_result: 'टूल के नतीजे',
  },
  policy: {
    title: 'क्या कैश होता है',
    version: 'नीति संस्करण {{version}}',
    use: '{{tier}} इस्तेमाल करें',
    ttl: 'कितनी देर रखें (मिनट)',
    ttlHint: '1 मिनट से 30 दिन (43200 मिनट) तक।',
    neverCached: 'सेटिंग कुछ भी हो, कभी कैश नहीं होता',
    save: 'नीति सहेजें',
    saved: 'नीति सहेजी गई। पुरानी नीति में रखे जवाब अब इस्तेमाल नहीं होते।',
  },
  inspect: {
    title: 'एक सवाल जाँचें',
    description: 'अगर कोई विज़िटर इसी सवाल से चैट शुरू करे तो क्या होगा। कुछ गिना या रखा नहीं जाता।',
    question: 'सवाल',
    language: 'जवाब की भाषा',
    auto: 'सवाल से पहचानें',
    run: 'जाँचें',
    result: 'नतीजा',
    status: {
      hit: 'कैश से जवाब मिलेगा',
      miss: 'अभी कैश नहीं है',
      stale: 'कैश किया जवाब पुराना है',
      bypass: 'कभी कैश नहीं होता',
      unavailable: 'कैश तक पहुँच नहीं है',
    },
    reasons: {
      personal_data: 'सवाल में निजी जानकारी है।',
      personalized: 'सिर्फ़ बातचीत का पहला संदेश साझा हो सकता है।',
      tier_disabled: 'इस ब्रांड के लिए हूबहू जवाब बंद हैं।',
      excluded_class: 'यह ऐसी जानकारी इस्तेमाल करता है जो कभी कैश नहीं होती।',
      excluded_by_rule: 'एक नियम इसे बाहर रखता है, जैसे चालू ऑर्डर के सवाल।',
      side_effects: 'यह कुछ बदल सकता है।',
      incomplete: 'जवाब पूरा नहीं था।',
      no_published_agent: 'ब्रांड का कोई प्रकाशित AI एजेंट नहीं है।',
      dependency_changed: 'रखने के बाद ज्ञान, एजेंट या नीति बदल गई।',
      store_unavailable: 'जब तक यह वापस न आए, जवाब नए सिरे से बनते हैं।',
      not_cached: 'इस सवाल का कोई जवाब अभी रखा नहीं गया।',
    },
    fingerprint: 'फ़िंगरप्रिंट',
    expires: 'समाप्ति',
    knowledge: 'ज्ञान के संस्करण',
    version: 'संस्करण {{version}}',
    preview: 'रखा हुआ जवाब',
    sources: 'स्रोत',
  },
  entries: {
    title: 'रखे हुए जवाब',
    description: 'सिर्फ़ फ़िंगरप्रिंट और स्रोत। सवाल और जवाब यहाँ नहीं दिखते।',
    empty: 'कोई रखा हुआ जवाब नहीं।',
    fingerprint: 'फ़िंगरप्रिंट',
    language: 'भाषा',
    sources: 'स्रोत',
    hits: 'दोबारा इस्तेमाल',
    stored: 'रखा गया',
    expires: 'समाप्ति',
    state: 'स्थिति',
    states: {
      active: 'इस्तेमाल में',
      stale: 'पुराना',
      invalidated: 'अब इस्तेमाल नहीं',
    },
    reason: 'वजह: {{reason}}',
    noSources: 'कोई स्रोत नहीं',
  },
  invalidate: {
    title: 'जवाबों का दोबारा इस्तेमाल रोकें',
    description: 'किसी सुधार के बाद इसका इस्तेमाल करें। पढ़ना तुरंत रुकता है; रखी हुई कॉपियाँ बाद में हटती हैं।',
    source: 'ये स्रोत देने वाले जवाब',
    allSources: 'इस ब्रांड के सभी रखे हुए जवाब',
    reason: 'वजह',
    reasonHint: 'ऑडिट लॉग में रखी जाती है।',
    impact: 'प्रभावित जवाब: {{count}}',
    submit: 'दोबारा इस्तेमाल रोकें',
    confirmTitle: 'इन जवाबों का दोबारा इस्तेमाल रोकें?',
    confirmBody: 'रखे हुए जवाब अब इस्तेमाल नहीं होंगे। अगले ग्राहकों को नए सिरे से बने जवाब मिलेंगे।',
    done: '{{count}} जवाबों का दोबारा इस्तेमाल रोका गया।',
    failed: '{{count}} रखी हुई कॉपियाँ अभी हटाई नहीं जा सकीं। वे अब पढ़ी नहीं जातीं।',
  },
  metrics: {
    title: 'इस्तेमाल और स्थिति',
    description: 'सिर्फ़ गिनती, दिन, प्रकार और भाषा के हिसाब से।',
    healthy: 'कैश तक पहुँच है।',
    degraded: 'कैश तक पहुँच नहीं है। जवाब नए सिरे से बनते हैं।',
    failures: 'पिछले 15 मिनट में विफल काम: {{count}}',
    degradedHint:
      'ग्राहकों को जवाब मिलते रहते हैं, पर खर्च ज़्यादा होता है। अगर गलत जवाब दोबारा इस्तेमाल हुए, तो ऊपर वह प्रकार बंद करें।',
    empty: 'अभी कोई इस्तेमाल दर्ज नहीं।',
    day: 'दिन',
    tier: 'प्रकार',
    language: 'भाषा',
    hits: 'दोबारा इस्तेमाल',
    misses: 'नए बने',
    bypasses: 'कैश लायक नहीं',
    stale: 'पुराने',
    errors: 'त्रुटियाँ',
    hitRate: 'दोबारा इस्तेमाल की दर',
    savedTokens: 'बचे टोकन',
    savedTime: 'बचा समय',
    noLanguage: 'कोई भी',
  },
};

export const cachingUr: CachingText = {
  nav: 'جوابات کی کیشنگ',
  title: 'جوابات کی کیشنگ',
  description:
    'عام ابتدائی سوالوں کے جوابات دوبارہ استعمال کریں، تاکہ گاہکوں کو وہ جلدی اور کم خرچ میں ملیں۔ ذاتی، جاری اور حساس سوال کبھی کیش نہیں ہوتے۔',
  brand: 'برانڈ',
  noBrands: 'پہلے ایک برانڈ شامل کریں۔',
  loading: 'جوابات کی کیشنگ لوڈ ہو رہی ہے',
  noManage: 'آپ دیکھ سکتے ہیں، لیکن کیشنگ صرف معلومات سنبھالنے والے لوگ بدل سکتے ہیں۔',
  tiers: {
    exact_answer: 'ہوبہو جوابات',
    embedding: 'تلاش کی ایمبیڈنگز',
    nlp: 'زبان کا تجزیہ',
    retrieval: 'تلاش کے نتائج',
    translation: 'ترجمے',
  },
  tierHints: {
    exact_answer:
      'ایک ہی پہلا سوال، ایک ہی زبان میں، تب تک ایک ہی جواب پاتا ہے جب تک اس کے پیچھے کی معلومات، ایجنٹ اور پالیسی نہ بدلیں۔',
    embedding: 'معلومات کی تلاش اسی سوال کے لیے نکالے گئے اعداد دوبارہ استعمال کرتی ہے۔',
    nlp: 'ابھی استعمال نہیں ہوتا۔',
    retrieval: 'ابھی استعمال نہیں ہوتا۔',
    translation: 'ابھی استعمال نہیں ہوتا۔',
  },
  classes: {
    general: 'عام',
    public_knowledge: 'عوامی معلومات',
    internal_knowledge: 'اندرونی معلومات',
    personal: 'ذاتی معلومات',
    identity: 'شناخت',
    account: 'اکاؤنٹ کی تفصیلات',
    memory: 'گاہک کی یادداشت',
    live_order: 'جاری آرڈر',
    payment: 'ادائیگیاں',
    approval: 'منظوریاں',
    tool_result: 'ٹول کے نتائج',
  },
  policy: {
    title: 'کیا کیش ہوتا ہے',
    version: 'پالیسی ورژن {{version}}',
    use: '{{tier}} استعمال کریں',
    ttl: 'کتنی دیر رکھیں (منٹ)',
    ttlHint: '1 منٹ سے 30 دن (43200 منٹ) تک۔',
    neverCached: 'ترتیبات جو بھی ہوں، کبھی کیش نہیں ہوتا',
    save: 'پالیسی محفوظ کریں',
    saved: 'پالیسی محفوظ ہو گئی۔ پرانی پالیسی میں رکھے جوابات اب استعمال نہیں ہوتے۔',
  },
  inspect: {
    title: 'ایک سوال جانچیں',
    description: 'اگر کوئی وزیٹر اسی سوال سے چیٹ شروع کرے تو کیا ہوگا۔ کچھ گنا یا رکھا نہیں جاتا۔',
    question: 'سوال',
    language: 'جواب کی زبان',
    auto: 'سوال سے پہچانیں',
    run: 'جانچیں',
    result: 'نتیجہ',
    status: {
      hit: 'کیش سے جواب ملے گا',
      miss: 'ابھی کیش نہیں',
      stale: 'کیش شدہ جواب پرانا ہے',
      bypass: 'کبھی کیش نہیں ہوتا',
      unavailable: 'کیش تک رسائی نہیں',
    },
    reasons: {
      personal_data: 'سوال میں ذاتی معلومات ہیں۔',
      personalized: 'صرف گفتگو کا پہلا پیغام شیئر ہو سکتا ہے۔',
      tier_disabled: 'اس برانڈ کے لیے ہوبہو جوابات بند ہیں۔',
      excluded_class: 'یہ ایسی معلومات استعمال کرتا ہے جو کبھی کیش نہیں ہوتیں۔',
      excluded_by_rule: 'ایک اصول اسے باہر رکھتا ہے، جیسے جاری آرڈر کے سوال۔',
      side_effects: 'یہ کچھ بدل سکتا ہے۔',
      incomplete: 'جواب مکمل نہیں تھا۔',
      no_published_agent: 'برانڈ کا کوئی شائع شدہ AI ایجنٹ نہیں۔',
      dependency_changed: 'رکھنے کے بعد معلومات، ایجنٹ یا پالیسی بدل گئی۔',
      store_unavailable: 'جب تک یہ واپس نہ آئے، جوابات نئے سرے سے بنتے ہیں۔',
      not_cached: 'اس سوال کا کوئی جواب ابھی رکھا نہیں گیا۔',
    },
    fingerprint: 'فنگر پرنٹ',
    expires: 'میعاد',
    knowledge: 'معلومات کے ورژن',
    version: 'ورژن {{version}}',
    preview: 'رکھا ہوا جواب',
    sources: 'ذرائع',
  },
  entries: {
    title: 'رکھے ہوئے جوابات',
    description: 'صرف فنگر پرنٹ اور ذرائع۔ سوال اور جواب یہاں نہیں دکھائے جاتے۔',
    empty: 'کوئی رکھا ہوا جواب نہیں۔',
    fingerprint: 'فنگر پرنٹ',
    language: 'زبان',
    sources: 'ذرائع',
    hits: 'دوبارہ استعمال',
    stored: 'رکھا گیا',
    expires: 'میعاد',
    state: 'حالت',
    states: {
      active: 'استعمال میں',
      stale: 'پرانا',
      invalidated: 'اب استعمال نہیں',
    },
    reason: 'وجہ: {{reason}}',
    noSources: 'کوئی ذریعہ نہیں',
  },
  invalidate: {
    title: 'جوابات کا دوبارہ استعمال روکیں',
    description:
      'کسی درستی کے بعد اسے استعمال کریں۔ پڑھنا فوراً رکتا ہے؛ رکھی ہوئی نقلیں بعد میں ہٹتی ہیں۔',
    source: 'یہ ذریعہ دینے والے جوابات',
    allSources: 'اس برانڈ کے تمام رکھے ہوئے جوابات',
    reason: 'وجہ',
    reasonHint: 'آڈٹ لاگ میں رکھی جاتی ہے۔',
    impact: 'متاثرہ جوابات: {{count}}',
    submit: 'دوبارہ استعمال روکیں',
    confirmTitle: 'ان جوابات کا دوبارہ استعمال روکیں؟',
    confirmBody:
      'رکھے ہوئے جوابات اب استعمال نہیں ہوں گے۔ اگلے گاہکوں کو نئے سرے سے بنے جوابات ملیں گے۔',
    done: '{{count}} جوابات کا دوبارہ استعمال روک دیا گیا۔',
    failed: '{{count}} رکھی ہوئی نقلیں ابھی ہٹائی نہیں جا سکیں۔ وہ اب پڑھی نہیں جاتیں۔',
  },
  metrics: {
    title: 'استعمال اور حالت',
    description: 'صرف گنتی، دن، قسم اور زبان کے حساب سے۔',
    healthy: 'کیش تک رسائی ہے۔',
    degraded: 'کیش تک رسائی نہیں۔ جوابات نئے سرے سے بنتے ہیں۔',
    failures: 'پچھلے 15 منٹ میں ناکام کام: {{count}}',
    degradedHint:
      'گاہکوں کو جوابات ملتے رہتے ہیں، لیکن خرچ زیادہ ہوتا ہے۔ اگر غلط جوابات دوبارہ استعمال ہوئے تو اوپر وہ قسم بند کریں۔',
    empty: 'ابھی کوئی استعمال درج نہیں۔',
    day: 'دن',
    tier: 'قسم',
    language: 'زبان',
    hits: 'دوبارہ استعمال',
    misses: 'نئے بنے',
    bypasses: 'کیش کے قابل نہیں',
    stale: 'پرانے',
    errors: 'غلطیاں',
    hitRate: 'دوبارہ استعمال کی شرح',
    savedTokens: 'بچائے گئے ٹوکن',
    savedTime: 'بچایا گیا وقت',
    noLanguage: 'کوئی بھی',
  },
};
